import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { ACCOUNTS_QUERY_KEY } from '../AccountsConstants';
import { useClientId } from '../../../_hooks/useClientId';
import { OptionType } from '../../../CommonComponents/OPL/Select/BaseSelect/types';
import {
  ContributionUnitDTO,
  EmployerLimitTypeDTO,
  EmployerMatchTypeDTO,
  MortgageTypeEnum,
  OwnerTypeDTO,
} from './useAccountsQuery';
import {
  AccountModalUsageContext,
  AccountModalUsageContextOptions,
} from '../Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import { ManualAccountsAssetAllocationTaxStatusIdDTO } from '../../../ManualAccountsAssetAllocation/types';

export type EarmarkOptionsDTO = OptionType<string>;

export interface OwnerOptionDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
}

export interface OwnerOptionsDTO {
  People: OwnerOptionDTO[];
  Trusts: OwnerOptionDTO[] | null;
  Businesses: OwnerOptionDTO[] | null;
}

type AssetAllocationTaxStatusesKeysDTO = 'Pre-tax' | 'Roth' | 'After-tax';
type ContributionTaxStatusesKeyDTO = 'Traditional' | 'Roth' | 'After-Tax';

interface TaxStatusesOptionsDTO<T> {
  Label: T;
  Value: number;
}

export enum AccountType {
  Checking = 0,
  Savings = 1,
  CD = 2,
  Annuity = 12,
  HSA = 21,
  Insurance = 20,
  IRA = 5,
  Roth = 7,
  TaxFree = 28,
  SepIra = 17,
  SimpleIra = 19,
  EmployerPlan = 27,
  E401K = 4,
  Roth401K = 22,
  E401A = 23,
  E403B = 10,
  E457 = 11,
  InheritedIra = 25,
  InheritedRoth = 26,
  E529 = 6,
  Brokerage = 3,
  EquityCompensation = 24,
  TaxDeferred = 30,
  CreditCard = 8,
  Mortgage = 13,
  Installment = 14,
  LineOfCredit = 15,
  StudentLoan = 16,
  AutoLoan = 29,
  DonorAdvisedFund = 31,
}

export interface AccountTypeOptionsDTO {
  GroupId: string;
  AccountType: AccountType;
  IsAsset: boolean;
  Label: string;
  AllowsUnequalOwnership: boolean;
  IsEmployerSponsoredRetirementAccount: boolean;
  IsEmployerMatchableRetirementAccount: boolean;
  TurnoverRatio: number;
  CapitalGainsRatio: number;
  EmployerMatchType: number | null;
  EmployerLimitType: number | null;
  CanEmployeeMaxContribution: boolean;
  HasContributionPeriodOption: boolean;
  HasContributionPercentOption: boolean;
  IsLiquid: boolean;
  AssetAllocationTaxStatuses: TaxStatusesOptionsDTO<AssetAllocationTaxStatusesKeysDTO>[];
  BalanceTaxStatuses: TaxStatusesOptionsDTO<AssetAllocationTaxStatusesKeysDTO>[];
  ContributionTaxStatuses: TaxStatusesOptionsDTO<ContributionTaxStatusesKeyDTO>[];
  EmployerContributionTaxStatuses: TaxStatusesOptionsDTO<AssetAllocationTaxStatusesKeysDTO>[];
  OwnerOptions: OwnerOptionsDTO;
}

interface AvailableAssetBucketsDTO {
  AssetTypes: number[];
  Description: string;
  Title: string;
}

type AccountTypes =
  | 'Checking'
  | 'Savings'
  | 'CD'
  | 'Annuity'
  | 'HSA'
  | 'Insurance'
  | 'IRA'
  | 'Roth'
  | 'TaxFree'
  | 'SepIra'
  | 'SimpleIra'
  | 'EmployerPlan'
  | 'E401K'
  | 'Roth401K'
  | 'E401A'
  | 'E403B'
  | 'E457'
  | 'InheritedIra'
  | 'InheritedRoth'
  | 'E529'
  | 'Brokerage'
  | 'TaxDeferred'
  | 'CreditCard'
  | 'AutoLoan'
  | 'Installment'
  | 'LineOfCredit'
  | 'StudentLoan'
  | 'EquityCompensation';

export type DefaultAssetBucketingIndicesDTO = Record<AccountTypes, number>;

export interface AssetAllocationDTO {
  id: number;
  name: string;
  y: number;
  color_group: number;
  color: string;
}
export interface BeneficiaryOptionDTO {
  Id: string;
  Name: string;
}

export enum DistributionStrategyTypeDTO {
  None = -1,
  PercentageOfAccountBalance,
  DollarAmount,
}

export interface CustomDistributionStrategyOptionsDTO {
  computationYearType: OptionType<number>[];
  distributionEndYearType: OptionType<string>[];
  distributionStartYearType: OptionType<string>[];
  distributionStrategyType: OptionType<DistributionStrategyTypeDTO>[];
}

export interface ManagementStyleDTO {
  id: number;
  name: string;
  defaultBucket: number | null;
}

interface TaxStatusDefaultAssetBucketingIndicesDTO {
  PreTax: number;
  AfterTax: number;
  Roth: number;
}

type SpecificAccountTypeKeys = 'E401K' | 'E403B' | 'SepIra' | 'SimpleIra';

type DefaultAssetBucketingIndicesByTaxStatusDTO = Record<
  Extract<AccountTypes, SpecificAccountTypeKeys>,
  TaxStatusDefaultAssetBucketingIndicesDTO
>;

interface DafFundingSourcesOptionDTO {
  label: string;
  value: {
    value: string;
    label: string;
  }[];
}

interface DonationYearOptionsDTO {
  label: string;
  value: number | null;
  type: number;
}

interface DafOptionsDTO {
  dafFundingSources: DafFundingSourcesOptionDTO[];
  donationStartYearOptions: DonationYearOptionsDTO[];
  donationEndYearOptions: DonationYearOptionsDTO[];
}

export interface OptionsDTO {
  AtLeast5PctOwnershipOptions: { label: string; value: boolean }[];
  EarmarkOptions: EarmarkOptionsDTO[];
  EducationEarmarkOptions: EarmarkOptionsDTO[];
  AllowAddManualAccount: boolean;
  IndividualOwnerOptions: OwnerOptionsDTO;
  AccountTypeOptions: AccountTypeOptionsDTO[];
  AvailableAssetBuckets: AvailableAssetBucketsDTO[];
  DefaultAssetBucketingIndices: DefaultAssetBucketingIndicesDTO[];
  AllowAssetBucketingCustomization: boolean;
  DisplayAssetBucketing: boolean;
  ContributionStartYearOptions: OptionType<number | null>[];
  ContributionEndYearOptions: OptionType<number | null>[];
  DefaultAssetAllocation: Record<ManualAccountsAssetAllocationTaxStatusIdDTO, AssetAllocationDTO[]>;
  BeneficiariesOptions: BeneficiaryOptionDTO[];
  CustomDistributionStrategyOptions: CustomDistributionStrategyOptionsDTO;
  SimpleIraEmployerMatchTypeOptions: OptionType<EmployerMatchTypeDTO | null>[];
  EmployerMatchOptions: OptionType<EmployerMatchTypeDTO | null>[];
  SimpleIraEmployerLimitTypeOptions: OptionType<EmployerLimitTypeDTO | null>[];
  EmployerLimitOptions: OptionType<EmployerLimitTypeDTO | null>[];
  EmployerMatchUnits: OptionType<ContributionUnitDTO | null>[];
  HasSpouse: boolean;
  IraDistributionMethodOptions: OptionType<number>[];
  IraStartDistributionYearOptions: OptionType<number>[];
  ShowExcludeFromDebtManagementGoal: boolean;
  PropertyOptions: { label: string; value: string }[];
  mortgageTypeOptions: { label: string; value: MortgageTypeEnum }[];
  AssetBucketingByManagementStyle: boolean;
  ManagementStyles: ManagementStyleDTO[];
  AnnuityYearOptions: number[];
  DistributionOptionOptions: OptionType<number>[];
  DefaultTurnoverRatio: number;
  DefaultCapitalGainsRatio: number;
  DefaultPercentageUsedInPlan: number;
  DefaultAssetBucketingIndicesByTaxStatus: DefaultAssetBucketingIndicesByTaxStatusDTO;
  DafOptions: DafOptionsDTO;
  ShowAnnuityPreTaxToggle: boolean;
  ShowAccountNickname: boolean;
  ShowHypotheticalReturn: boolean;
  AnnuityLifeExpectancyPrincipal: number;
  AnnuityLifeExpectancySpouse: number;
}

interface AccountOptionsDTO {
  Options: OptionsDTO;
}

const fetchAccountOptions = async (
  clientId: number,
  usageContext?: AccountModalUsageContext,
  usageContextOptions?: any,
): Promise<AccountOptionsDTO> => {
  const response = await authFetch(
    urlWithParams(
      '/Consumer/NewAccounts/options',
      {
        clientId,
        workflow: usageContext,
        ...usageContextOptions,
      },
      { skipNulls: true },
    ),
  );

  return {
    ...response,
    Options: {
      ...response.Options,
      CustomDistributionStrategyOptions: {
        ...response.Options.CustomDistributionStrategyOptions,
        distributionStrategyType: [
          { label: 'None', value: DistributionStrategyTypeDTO.None },
          ...response.Options.CustomDistributionStrategyOptions.distributionStrategyType,
        ],
      },
    },
  };
};

export const ACCOUNT_OPTIONS_QUERY_KEY = 'accountOptions';

interface QueryParams {
  usageContext?: AccountModalUsageContext;
  usageContextOptions?: AccountModalUsageContextOptions;
}

export const useAccountsOptionsQuery = ({
  usageContext,
  usageContextOptions,
}: QueryParams = {}) => {
  const clientId = useClientId();

  return useQuery(
    [ACCOUNTS_QUERY_KEY, ACCOUNT_OPTIONS_QUERY_KEY, clientId, usageContext, usageContextOptions],

    () => fetchAccountOptions(clientId, usageContext, usageContextOptions),

    {
      cacheTime: 0,
    },
  );
};
